// src/components/sections/Hero.styles.js

const styles = {
    section: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '5rem',
      paddingBottom: '5rem',
      position: 'relative',
      overflow: 'hidden'
    },
    
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    
    contentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gap: '2rem',
      width: '100%'
    },
    
    leftContent: {
      gridColumn: '1 / span 12',
      '@media (min-width: 1024px)': {
        gridColumn: '1 / span 6'
      },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    
    heading: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: 800,
      lineHeight: 1.2,
      marginBottom: '1.5rem',
      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      padding:'6px'
    },
    
    subheading: (isDarkMode) => ({
      fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
      color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
      marginBottom: '2rem',
      maxWidth: '40rem'
    }),
    
    buttonContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '3rem'
    },
    
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      textDecoration: 'none',
      fontWeight: 500,
      fontSize: '1rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'transform 0.2s ease, background-color 0.2s ease',
      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
    },
    
    secondaryButton: (isDarkMode) => ({
      backgroundColor: 'transparent',
      color: isDarkMode ? '#f9fafb' : '#1f2937',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      textDecoration: 'none',
      fontWeight: 500,
      fontSize: '1rem',
      display: 'inline-flex',
      alignItems: 'center',
      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
      transition: 'transform 0.2s ease, background-color 0.2s ease',
    }),
    
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '1.5rem'
    },
    
    statCard: (isDarkMode) => ({
      backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)',
      backdropFilter: 'blur(8px)',
      borderRadius: '0.5rem',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
    }),
    
    statIcon: {
      color: '#3b82f6',
      marginBottom: '0.5rem',
      borderRadius: '50%',
      padding: '0.5rem',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    
    statValue: (isDarkMode) => ({
      fontWeight: 700,
      fontSize: '1.25rem',
      color: isDarkMode ? '#f9fafb' : '#1f2937',
    }),
    
    statLabel: (isDarkMode) => ({
      fontSize: '0.875rem',
      color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    }),
    
    rightContent: {
      gridColumn: '1 / span 12',
      '@media (min-width: 1024px)': {
        gridColumn: '7 / span 6',
        marginTop: 0
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '3rem',
    },
    
    codeContainer: (isDarkMode) => ({
      backgroundColor: isDarkMode ? '#1f2937' : 'white',
      borderRadius: '0.75rem',
      boxShadow: isDarkMode 
        ? '0 8px 30px rgba(0, 0, 0, 0.5)' 
        : '0 8px 30px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '500px',
      overflow: 'hidden',
      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
    }),
    
    codeHeader: (isDarkMode) => ({
      padding: '0.75rem 1rem',
      backgroundColor: isDarkMode ? '#111827' : '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
    }),
    
    codeDots: { 
      display: 'flex', 
      gap: '0.5rem', 
      marginRight: '1rem' 
    },
    
    redDot: { 
      width: '12px', 
      height: '12px', 
      borderRadius: '50%', 
      backgroundColor: '#f87171' 
    },
    
    yellowDot: { 
      width: '12px', 
      height: '12px', 
      borderRadius: '50%', 
      backgroundColor: '#fbbf24' 
    },
    
    greenDot: { 
      width: '12px', 
      height: '12px', 
      borderRadius: '50%', 
      backgroundColor: '#34d399' 
    },
    
    codeHeaderText: (isDarkMode) => ({ 
      fontSize: '0.875rem', 
      fontWeight: 500,
      color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'
    }),
    
    codeBlock: (isDarkMode) => ({
      padding: '1.5rem',
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      overflowX: 'auto',
      backgroundColor: isDarkMode ? '#111827' : '#f8fafc',
    }),
    
    responseBlock: (isDarkMode) => ({
      padding: '1.5rem',
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      backgroundColor: isDarkMode ? '#0f172a' : '#f1f5f9',
      borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
    }),
    
    responseLabel: {
      color: 'rgba(255,255,255,0.6)',
      marginBottom: '0.5rem',
      fontSize: '0.75rem'
    },
    
    codePre: (isDarkMode) => ({
      margin: 0,
      color: isDarkMode ? '#f9fafb' : '#1f2937',
    }),
  };
  
  export default styles;